import { memo, type ReactElement, useState } from 'react';
import type { BacktestingConfiguration } from '../BacktestConfiguration.types.ts';
import { Stack } from '@mui/joy';
import { IWindowType, usePortfolioCorrelationsQuery } from '../../../../../generated/graphql.tsx';
import { defaultRowSpacing } from '../../../../StackSpacing.ts';
import { useDebounce } from '../../../../UseDebounce.ts';
import { isNil } from 'lodash/fp';
import { HistoricalPortfolioCorrelations } from '../../../../portfolio/correlations/HistoricalPortfolioCorrelations.tsx';
import GInput from 'components/technical/inputs/GInput/GInput.tsx';
import { Select } from '../../../../technical/inputs';
import type { LatestCorrelationData } from 'components/technical/charts/CorrelationChart/CorrelationChart.tsx';
import { LatestPortfolioCorrelations } from '../../../../portfolio/correlations/LatestPortfolioCorrelations.tsx';
import {
  isWindowValid,
  windowTypeOptions,
  isWarmUpEnabled,
} from '../../../../portfolio/correlations/PortfolioCorrelations.utils.ts';

interface PortfolioCorrelationSectionProps {
  backtestingConfig: BacktestingConfiguration;
}

const PortfolioCorrelations = ({ backtestingConfig }: PortfolioCorrelationSectionProps): ReactElement => {
  const [windowType, setWindowType] = useState(IWindowType.Expanding);
  const [window, setWindow] = useState<string | null>('30');
  const debouncedWindow = useDebounce(window, 300);

  const queryOutput = usePortfolioCorrelationsQuery({
    variables: {
      input: {
        portfolios: backtestingConfig.portfolios.map((p) => p.id) ?? [],
        dateRange: backtestingConfig.range,
        windowParams: {
          windowType: windowType,
          windowSize: windowType !== IWindowType.Expanding ? Number.parseInt(debouncedWindow ?? '') : undefined,
        },
      },
    },
    skip: !isWindowValid(windowType, debouncedWindow),
  });

  const queryCorrelationData = queryOutput.data?.portfolio.portfolioCorrelations ?? [];

  const latestCorrelations: LatestCorrelationData[] = queryCorrelationData.flatMap((pc) => {
    const lastValue = pc.correlations.at(-1)?.value;
    if (isNil(lastValue)) {
      return [];
    }
    return [
      {
        value: lastValue,
        firstElement: pc.portfolioPair.portfolioA,
        secondElement: pc.portfolioPair.portfolioB,
      },
    ];
  });

  const isExpanding = isWarmUpEnabled(windowType);

  return (
    <Stack rowGap={defaultRowSpacing}>
      <Stack direction={'row'} columnGap={1} alignSelf={'flex-end'}>
        {!isExpanding && (
          <GInput
            error={isWindowValid(windowType, window) ? '' : 'Invalid value'}
            showLabelAboveInput
            label="Window"
            value={window ?? ''}
            type={'number'}
            onChange={(value): void => setWindow(value)}
            width="normal"
          />
        )}
        <Select
          showLabelAboveInput
          label="Type"
          value={windowType}
          onChange={(value): void => setWindowType(value)}
          options={windowTypeOptions}
          width="normal"
        />
      </Stack>
      <LatestPortfolioCorrelations data={latestCorrelations} loading={queryOutput.loading} error={queryOutput.error} />
      <HistoricalPortfolioCorrelations warmupEnabled={isExpanding} {...queryOutput} />
    </Stack>
  );
};

export default memo(PortfolioCorrelations);
